/**
 * Candidatos — tipos y etiquetas de la bolsa de empleo.
 *
 * Gemelo de `server/src/services/candidatos/esquema.ts`. Al añadir un estado,
 * un motivo de descarte o una fuente allí, hay que añadirlos aquí o la pantalla
 * enseñará el código crudo. No hay paquete compartido entre cliente y servidor;
 * es el mismo trato que tienen `lib/peticiones.ts` y la web.
 *
 * Los PUESTOS y las ZONAS no están aquí a propósito: son ajustes editables
 * desde Configuración y se leen con `api.getSettings()`.
 */

export type EstadoCandidato =
  | 'nuevo'
  | 'en_bolsa'
  | 'preseleccionado'
  | 'entrevista'
  | 'contratado'
  | 'descartado';

export type MotivoDescarte =
  | 'perfil_no_encaja'
  | 'sin_vehiculo'
  | 'zona'
  | 'no_disponible'
  | 'no_contesta'
  | 'rechazo_del_candidato'
  | 'otro';

export type Fuente =
  | 'web'
  | 'email'
  | 'whatsapp'
  | 'presencial'
  | 'infojobs'
  | 'indeed'
  | 'linkedin'
  | 'referido'
  | 'otro';

export type Dedicacion = 'todo_el_anio' | 'temporada' | 'puntual' | 'sin_especificar';
export type Jornada = 'completa' | 'parcial' | 'fines_de_semana' | 'horas_sueltas';
export type NivelIdioma = 'basico' | 'medio' | 'alto' | 'nativo';
export type Ternario = 'si' | 'no' | 'desconocido';
export type PermisoTrabajo = 'si' | 'en_tramite' | 'no' | 'desconocido';
export type TipoDocumento = 'cv' | 'carta' | 'certificado' | 'otro';
export type ModalidadEntrevista = 'presencial' | 'telefono' | 'video';
export type OrigenConsentimiento =
  | 'formulario_web'
  | 'enviado_por_candidato'
  | 'acuse_email'
  | 'verbal'
  | 'confirmo_seguir'
  | 'no_consta';
export type ViaRenovacion = 'correo' | 'whatsapp' | 'llamada' | 'en_persona';
export type PlantillaCorreo = 'acuse' | 'entrevista' | 'descarte';

export interface DocumentoCandidato {
  id: string;
  tipo: TipoDocumento;
  fileName: string;
  blobPath: string;
  contentType: string;
  size: number;
  uploadedAt: string;
  uploadedBy?: string;
  extraccion?: 'ok' | 'sin_texto' | 'no_soportado' | 'error' | null;
  /** El servidor no manda el texto del CV, solo si lo tiene. */
  tieneTexto?: boolean;
}

export interface PropuestaIA {
  resumen: string;
  puestosSugeridos: string[];
  experiencia: string[];
  experienciaAnios: number | null;
  puntosFuertes: string[];
  alertas: string[];
  campos: {
    nombre?: string;
    apellidos?: string;
    email?: string;
    telefono?: string;
    localidad?: string;
    idiomas?: { codigo: string; nivel: NivelIdioma }[];
    formacion?: string;
    vehiculo?: Ternario;
    carnet?: Ternario;
    permisoTrabajo?: PermisoTrabajo;
    experienciaAnios?: number;
    experiencia?: string;
  };
  idiomaCv: string;
  modelo: string;
  at: string;
  /** `leyendo` mientras el modelo trabaja; la ficha lo va consultando. */
  estado: 'leyendo' | 'ok' | 'sin_texto' | 'error';
  detalle?: string;
}

export interface Candidato {
  id: string;
  type: 'candidato';

  nombre: string;
  apellidos: string;
  email?: string;
  telefono?: string;
  telefonoNormalizado?: string;
  localidad?: string;
  permisoTrabajo?: PermisoTrabajo;
  idiomas: { codigo: string; nivel: NivelIdioma }[];

  puestos: string[];
  zonas: string[];
  experienciaAnios?: number;
  experiencia?: string;
  formacion?: string;
  vehiculo: Ternario;
  carnet: Ternario;
  disponibilidad: { desde?: string; dedicacion: Dedicacion; jornada: Jornada[] };
  pretensionSalarial?: string;

  estado: EstadoCandidato;
  motivoDescarte?: MotivoDescarte;
  valoracion?: number | null;
  etiquetas: string[];
  fuente: Fuente;
  fuenteDetalle?: string;
  vacanteId?: string;
  notas?: string;

  documentos: DocumentoCandidato[];
  cvPrincipalId?: string;
  /** Completa solo en la ficha. El listado manda `estadoIa` y `resumenIa`. */
  ia?: PropuestaIA;
  tieneIa?: boolean;
  estadoIa?: PropuestaIA['estado'];
  resumenIa?: string;

  entrevista?: { fecha: string; lugar?: string; modalidad: ModalidadEntrevista; tareaId?: string };
  contratacion?: { fecha: string; puesto?: string; nota?: string };
  correo?: Partial<
    Record<PlantillaCorreo, { estado: 'enviado' | 'error' | 'no_configurado'; detalle?: string | null; at: string }>
  >;

  consentimiento: { fuente: OrigenConsentimiento; at?: string };
  conservarHasta: string;
  caducado?: boolean;

  createdAt: string;
  updatedAt: string;
  createdBy?: string;
  updatedBy?: string;
}

export interface DuplicadoCandidato {
  id: string;
  nombre: string;
  apellidos: string;
  estado: EstadoCandidato;
  email?: string;
  telefono?: string;
  createdAt: string;
  coincidePor: ('email' | 'telefono')[];
}

export interface ContadoresCandidatos {
  /** Las localidades que hay en la base, para el desplegable del filtro. */
  localidades: string[];
  all: number;
  caducados: number;
  nuevo: number;
  en_bolsa: number;
  preseleccionado: number;
  entrevista: number;
  contratado: number;
  descartado: number;
}

export interface CambioEstadoCandidato {
  estado: EstadoCandidato;
  motivo?: MotivoDescarte;
  nota?: string;
  entrevista?: { fecha: string; lugar?: string; modalidad?: ModalidadEntrevista };
  contratacion?: { fecha: string; puesto?: string; nota?: string };
  vacanteId?: string | null;
}

// ─── Etiquetas ────────────────────────────────────────────────────────────

export const ESTADO_LABELS: Record<EstadoCandidato, string> = {
  nuevo: 'Nuevo',
  en_bolsa: 'En bolsa',
  preseleccionado: 'Preseleccionado',
  entrevista: 'Entrevista',
  contratado: 'Contratado',
  descartado: 'Descartado',
};

export const ESTADO_BADGES: Record<EstadoCandidato, string> = {
  nuevo: 'bg-blue-100 text-blue-700',
  en_bolsa: 'bg-sereno-blue text-sereno-dark',
  preseleccionado: 'bg-amber-100 text-amber-700',
  entrevista: 'bg-purple-100 text-purple-700',
  contratado: 'bg-green-100 text-green-700',
  descartado: 'bg-gray-100 text-gray-500',
};

export const MOTIVO_DESCARTE_LABELS: Record<MotivoDescarte, string> = {
  perfil_no_encaja: 'El perfil no encaja',
  sin_vehiculo: 'Sin vehículo para la zona',
  zona: 'Zona incompatible',
  no_disponible: 'No está disponible cuando hace falta',
  no_contesta: 'No contesta',
  rechazo_del_candidato: 'Lo rechazó el candidato',
  otro: 'Otro',
};

export const FUENTE_LABELS: Record<Fuente, string> = {
  web: 'Formulario de la web',
  email: 'Correo',
  whatsapp: 'WhatsApp',
  presencial: 'En mano',
  infojobs: 'InfoJobs',
  indeed: 'Indeed',
  linkedin: 'LinkedIn',
  referido: 'Recomendado',
  otro: 'Otro',
};

export const DEDICACION_LABELS: Record<Dedicacion, string> = {
  todo_el_anio: 'Todo el año',
  temporada: 'Solo temporada',
  puntual: 'Puntual / refuerzos',
  sin_especificar: 'Sin especificar',
};

export const JORNADA_LABELS: Record<Jornada, string> = {
  completa: 'Jornada completa',
  parcial: 'Media jornada',
  fines_de_semana: 'Fines de semana',
  horas_sueltas: 'Horas sueltas',
};

export const NIVEL_IDIOMA_LABELS: Record<NivelIdioma, string> = {
  basico: 'básico',
  medio: 'medio',
  alto: 'alto',
  nativo: 'nativo',
};

export const TERNARIO_LABELS: Record<Ternario, string> = {
  si: 'Sí',
  no: 'No',
  desconocido: 'No consta',
};

export const PERMISO_TRABAJO_LABELS: Record<PermisoTrabajo, string> = {
  si: 'Sí',
  en_tramite: 'En trámite',
  no: 'No',
  desconocido: 'No consta',
};

export const TIPO_DOCUMENTO_LABELS: Record<TipoDocumento, string> = {
  cv: 'Currículum',
  carta: 'Carta de presentación',
  certificado: 'Certificado o título',
  otro: 'Otro',
};

export const MODALIDAD_LABELS: Record<ModalidadEntrevista, string> = {
  presencial: 'Presencial',
  telefono: 'Por teléfono',
  video: 'Videollamada',
};

export const CONSENTIMIENTO_LABELS: Record<OrigenConsentimiento, string> = {
  formulario_web: 'Marcó la casilla en el formulario de la web',
  enviado_por_candidato: 'Nos mandó el currículum él mismo',
  acuse_email: 'Se le informó en el correo de acuse de recibo',
  verbal: 'Lo dio de palabra',
  confirmo_seguir: 'Nos pidió seguir en la bolsa',
  no_consta: 'No consta',
};

/** Por dónde nos pidió el candidato seguir en la bolsa (botón de renovar). */
export const VIA_RENOVACION_LABELS: Record<ViaRenovacion, string> = {
  correo: 'Correo',
  whatsapp: 'WhatsApp',
  llamada: 'Llamada',
  en_persona: 'En persona',
};

/** Idiomas frecuentes en la Costa de la Luz, por si sirven de atajo. */
export const IDIOMAS_LABELS: Record<string, string> = {
  es: 'Español',
  en: 'Inglés',
  de: 'Alemán',
  fr: 'Francés',
  pt: 'Portugués',
  it: 'Italiano',
  ar: 'Árabe',
  ro: 'Rumano',
};

/** Una etiqueta legible, o el código crudo si alguien añadió una opción nueva. */
export function etiqueta(mapa: Record<string, string>, codigo?: string | null): string | undefined {
  if (!codigo) return undefined;
  return mapa[codigo] ?? codigo;
}

/** Nombre y apellidos, o «Sin nombre» si la ficha aún no los tiene. */
export function nombreCompleto(c: Pick<Candidato, 'nombre' | 'apellidos'>): string {
  return [c.nombre, c.apellidos].map((t) => (t || '').trim()).filter(Boolean).join(' ') || 'Sin nombre';
}

/** El documento marcado como CV principal, o el primero de tipo `cv`. */
export function cvPrincipal(c: Candidato): DocumentoCandidato | undefined {
  if (c.cvPrincipalId) {
    const marcado = c.documentos?.find((d) => d.id === c.cvPrincipalId);
    if (marcado) return marcado;
  }
  return c.documentos?.find((d) => d.tipo === 'cv');
}

/** Si el plazo de conservación ya ha vencido, mire lo que mire el barrido. */
export function plazoVencido(c: Candidato): boolean {
  if (c.estado === 'contratado' || !c.conservarHasta) return false;
  return c.conservarHasta < new Date().toISOString();
}

/** Número para `wa.me`, que exige dígitos con prefijo y sin el `+`. */
export function urlWhatsApp(c: Candidato): string | undefined {
  return c.telefonoNormalizado ? `https://wa.me/${c.telefonoNormalizado}` : undefined;
}

/**
 * La experiencia en una línea, para leerla de un vistazo en la lista.
 *
 * Se prefiere el resumen de la lectura del CV, que es una frase pensada para
 * esto; si no lo hay, la primera línea de lo que haya escrito una persona.
 * Vale igual con la ficha completa (`ia.resumen`) que con una fila del listado
 * (`resumenIa`), que es la misma frase con otro nombre.
 */
export function experienciaBreve(c: Candidato): string | undefined {
  const resumen = (c.resumenIa || c.ia?.resumen || '').trim();
  if (resumen) return resumen;
  const primera = (c.experiencia || '').split('\n').map(l => l.trim()).find(Boolean);
  return primera || undefined;
}

export function formatearTamano(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Búsqueda tolerante a mayúsculas y tildes: «Jose» encuentra a «José». */
export function normalizar(texto: string): string {
  return texto.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}
